import {Component, computed, DestroyRef, effect, inject} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {ProjectStepStateService} from '../project-step-state.service';
import {Chapter, Step, StepStatus} from '../project-step.types';
import {PanelMenu} from 'primeng/panelmenu';
import {NgClass, NgStyle} from '@angular/common';
import {Ripple} from 'primeng/ripple';
import {Badge} from 'primeng/badge';
import {ProgressBar} from 'primeng/progressbar';
import {ActivatedRoute, Router} from '@angular/router';
import {ButtonModule} from 'primeng/button';
import {Divider} from 'primeng/divider';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TooltipModule} from 'primeng/tooltip';
import {ProjectDataService} from '../../../../shared/services/project-data.service';
import {
  ProjectRoleAccessDirective,
  ProjectRoleConfig,
} from '../../../../shared/directives/project-role-access.directive';
import {ProjectChapterType, ProjectStepType, ProjectUserDetailResponse} from '@frontend/shared-api';
import {ProjectRoleConfigService} from '../../../../shared/services/project-role-config.service';
import {ProjectSideMenuAccessControlService} from './project-side-menu-access-control.service';
import {SPECIAL_OPTION_IDS} from '../project-step-state.constants';
import {APP_ROUTES} from '../../../../app.routes';
import {ProjectUserDetailService} from '../../../../shared/services/project-user-detail.service';
import {SaveButtonTrackingService} from '../../../../shared/project-step-save-button/save-button-tracking.service';
import {ConfirmationDialogService} from '../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-project-side-menu',
  templateUrl: './project-side-menu.component.html',
  imports: [
    PanelMenu,
    Ripple,
    Badge,
    ButtonModule,
    ProgressBar,
    Divider,
    NgClass,
    NgStyle,
    TranslocoDirective,
    TooltipModule,
    ProjectRoleAccessDirective,
    TranslocoPipe,
  ],
})
export class ProjectSideMenuComponent {
  menuItems: MenuItem[] = [];
  isCollapsed = false;
  protected readonly stepState = inject(ProjectStepStateService);
  protected readonly projectDataService = inject(ProjectDataService);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly roleConfigService = inject(ProjectRoleConfigService);
  private readonly projectUserDetailService = inject(ProjectUserDetailService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accessControlService = inject(ProjectSideMenuAccessControlService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly saveButtonTrackingService = inject(SaveButtonTrackingService);

  protected readonly activeSpecialOption = computed(() => this.stepState.activeSpecialOption());
  protected readonly projectId = computed(() => this.projectDataService.getProject()()?.id);
  private readonly userDetail = this.projectUserDetailService.createUserDetailSignal(
    this.projectId,
    this.destroyRef,
  );

  protected readonly projectManagerAndTenantAdminAccessConfig = computed(
    (): ProjectRoleConfig | null => {
      const id = this.projectId();
      return id
        ? this.roleConfigService.createProjectManagerWithTenantAdminConfig(id, this.userDetail())
        : null;
    },
  );
  protected readonly activeStepChapterStepId = computed(() => {
    const activeStep = this.stepState.activeStep();
    const activeOption = this.activeSpecialOption();

    if (activeStep && !activeOption) {
      return `${activeStep.chapterId}.${activeStep.stepId}`;
    }
    return null;
  });

  constructor() {
    effect(() => {
      const chapters = this.stepState.chaptersWithStatuses();
      const userDetail = this.userDetail();
      const activeStep = this.stepState.activeStep();

      this.stepState.activeSpecialOption();
      this.menuItems = this.buildMenuModel(chapters, activeStep);

      this.redirectToFirstAccessibleStepIfNeeded(chapters, userDetail, activeStep);
    });
  }

  private buildMenuModel(
    chapters: Chapter[],
    activeStep: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null,
  ): MenuItem[] {
    const activeChapterId = activeStep?.chapterId || ProjectChapterType.NOT_IMPLEMENTED;
    const activeStepId = activeStep?.stepId || ProjectStepType.NOT_IMPLEMENTED;
    const activeOptionId = this.activeSpecialOption();

    return chapters.map((chap) => {
      const chapterStatus = this.buildChapterStatus(chap.steps);
      const badgeSeverity = this.buildChapterBadge(chapterStatus);
      const stepItems = this.buildStepItems(chap, activeStepId, activeChapterId, activeOptionId);

      const isActiveChapter =
        chap.configId === activeChapterId &&
        chap.steps.some((s) => s.configId === activeStepId) &&
        !activeOptionId;

      const isChapterAccessible = this.accessControlService.isChapterAccessible(
        chap,
        this.userDetail(),
      );

      return {
        id: chap.configId,
        directusId: chap.directusId,
        label: `${chap.chapterUiNumber}. ${chap.translatedTitle}`,
        icon: chap.icon,
        status: chapterStatus,
        badge: `${chap.steps.length}`,
        badgeSeverity: badgeSeverity,
        items: stepItems,
        expanded: isActiveChapter,
        active: isActiveChapter,
        accessible: isChapterAccessible,
      };
    });
  }

  private buildChapterStatus(chapterSteps: Step[]) {
    const total = chapterSteps.length;
    const completed = chapterSteps.filter((s) => s.status === StepStatus.COMPLETED).length;
    const inProgress = chapterSteps.filter((s) => s.status === StepStatus.IN_PROGRESS).length;

    let chapterStatus = StepStatus.NOT_STARTED;
    if (completed === total) {
      chapterStatus = StepStatus.COMPLETED;
    } else if (completed > 0 || inProgress > 0) {
      chapterStatus = StepStatus.IN_PROGRESS;
    }
    return chapterStatus;
  }

  private buildChapterBadge(chapterStatus: StepStatus) {
    let badgeSeverity = 'secondary';
    if (chapterStatus === StepStatus.COMPLETED) {
      badgeSeverity = 'success';
    } else if (chapterStatus === StepStatus.IN_PROGRESS) {
      badgeSeverity = 'info';
    }
    return badgeSeverity;
  }

  private buildStepItems(
    chap: Chapter,
    activeStepId: ProjectStepType,
    activeChapterId: ProjectChapterType,
    activeOptionId: string | null,
  ) {
    const stepItems: MenuItem[] = chap.steps.map((step: Step) => {
      const isAccessible = this.accessControlService.isStepAccessible(
        chap.configId,
        step.configId,
        this.userDetail(),
      );

      const setActiveStepAndClearOption = () => {
        this.stepState.clearActiveSpecialOption();
        this.stepState.setActiveStep(chap.configId, step.configId);
      };

      return {
        id: step.configId.toString(),
        directusId: step.directusId,
        label: `${chap.chapterUiNumber}.${step.stepUiNumber}. ${step.translatedTitle}`,
        chapterStep: `${chap.configId}.${step.configId}`,
        status: step.status,
        accessible: isAccessible,
        locked: this.stepState.isStepLocked(chap.configId, step.configId),
        command: () => {
          if (!isAccessible) {
            return;
          }
          // Clear any active special option when selecting a regular step
          if (this.stepState.isNavigationConfirmationRequired()) {
            this.confirmDialogService.confirm({
              headerKey: `project.confirmStepChange.header`,
              messageKey: `project.confirmStepChange.message`,
              accept: setActiveStepAndClearOption,
              reject: () => this.saveButtonTrackingService.requestReveal(),
            });
          } else {
            setActiveStepAndClearOption();
          }
        },
        ...(step.configId === activeStepId &&
          chap.configId === activeChapterId &&
          !activeOptionId && {active: true}),
      };
    });
    return stepItems;
  }

  backToProject() {
    const projectSignal = this.projectDataService.getProject();
    if (projectSignal) {
      const projectData = projectSignal();
      if (projectData?.id) {
        this.router.navigate([APP_ROUTES.PROJECT, projectData.id]);
        return;
      }
    }
    this.router.navigate([APP_ROUTES.PROJECT]);
  }

  showStepDescription(event: Event, directusId: string): void {
    event.stopPropagation();
    this.stepState.openStepDescription(directusId);
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }

  navigateToUserManagement() {
    this.stepState.setActiveSpecialOption(SPECIAL_OPTION_IDS.USER_MANAGEMENT);
  }

  navigateToProviderCommunication() {
    this.stepState.setActiveSpecialOption(SPECIAL_OPTION_IDS.PROVIDER_COMMUNICATION);
  }

  private redirectToFirstAccessibleStepIfNeeded(
    chapters: Chapter[],
    userDetail: ProjectUserDetailResponse | undefined,
    activeStep: {chapterId: ProjectChapterType; stepId: ProjectStepType} | null,
  ): void {
    const redirectTarget = this.accessControlService.getRedirectTarget(
      chapters,
      userDetail,
      activeStep,
    );

    if (redirectTarget === SPECIAL_OPTION_IDS.USER_MANAGEMENT) {
      this.stepState.setActiveSpecialOption(SPECIAL_OPTION_IDS.USER_MANAGEMENT);
    } else if (redirectTarget) {
      this.stepState.setActiveStep(redirectTarget.chapterId, redirectTarget.stepId);
    }
  }

  isUserManagement(): boolean {
    return this.activeSpecialOption() === SPECIAL_OPTION_IDS.USER_MANAGEMENT;
  }

  protected isRfiInvitationsStepCompleted(): boolean {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFI, stepId: ProjectStepType.RFI_INVITATION_LIST},
    ]);
  }
}
